import React from 'react';
import { ParticipantRole, SpeakerColor } from '../config/participants';
import { formatFirstLine, formatTurns } from '../constants/participantReview';
import { ReviewRow, RowEdit, truncateQuote } from '../utils/participantReview';

const ROLE_OPTIONS: Array<{ value: ParticipantRole; label: string }> = [
  { value: ParticipantRole.SALESPERSON, label: 'Salesperson' },
  { value: ParticipantRole.CUSTOMER, label: 'Customer' },
];

interface RoleToggleProps {
  label: string;
  value: ParticipantRole;
  onChange: (role: ParticipantRole) => void;
}

const RoleToggle: React.FC<RoleToggleProps> = ({ label, value, onChange }) => (
  <div role="radiogroup" aria-label={label} className="inline-flex gap-1 p-1 bg-surface-subtle rounded-control flex-shrink-0">
    {ROLE_OPTIONS.map((option) => {
      const isSelected = option.value === value;
      return (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={isSelected}
          onClick={() => onChange(option.value)}
          className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signature ${
            isSelected ? 'bg-surface text-signature shadow-sm' : 'text-ink-muted hover:text-ink'
          }`}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

/** 12px speaker colour dot, nudged down to sit on the name field's first line. */
const ColorDot: React.FC<{ color: SpeakerColor }> = ({ color }) => (
  <span
    aria-hidden="true"
    className="w-3 h-3 mt-2 rounded-full flex-shrink-0"
    style={{ backgroundColor: color?.bg, border: `1px solid ${color?.border}` }}
  />
);

interface ParticipantReviewRowProps {
  row: ReviewRow;
  edit: RowEdit;
  /** The row's original name, so control labels stay stable while the name is edited. */
  rowLabel: string;
  color: SpeakerColor;
  isSaving: boolean;
  onChange: (change: Partial<RowEdit>) => void;
}

/** dot · [name / first line] · Salesperson|Customer · N turns — top-aligned, as on the macOS sheet. */
const ParticipantReviewRow: React.FC<ParticipantReviewRowProps> = ({
  row,
  edit,
  rowLabel,
  color,
  isSaving,
  onChange,
}) => (
  <li className="flex items-start gap-2.5 border border-hairline rounded-control px-2 py-1.5">
    <ColorDot color={color} />
    <div className="flex-1 min-w-0">
      <input
        value={edit?.name ?? ''}
        onChange={(event) => onChange({ name: event.target.value })}
        aria-label={`Name for ${rowLabel}`}
        maxLength={100}
        disabled={isSaving}
        className="w-full text-xs border border-ink-border rounded-control px-2 py-1 bg-surface focus:ring-2 focus:ring-signature outline-none"
      />
      {row?.firstLine && (
        <p className="mt-0.5 px-0.5 text-xs text-ink-muted truncate" title={row.firstLine}>
          {formatFirstLine(truncateQuote(row.firstLine))}
        </p>
      )}
    </div>
    <RoleToggle label={`Role for ${rowLabel}`} value={edit?.role ?? row.role} onChange={(role) => onChange({ role })} />
    <span className="w-14 flex-shrink-0 py-1.5 text-right text-xs text-ink-muted whitespace-nowrap">
      {formatTurns(row.turnCount)}
    </span>
  </li>
);

export default ParticipantReviewRow;
